import * as XLSX from 'xlsx';
import tableDesign from 'themes/table';
import MainCard from 'components/MainCard';
import { Empty, Flex, Tooltip } from 'antd';
import { useCallback, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { generateExcelFileName } from 'utils/utils';
import TablePagination from './DataTablePagination';
import { tableFontSize } from 'components/StyleConstant';
import ExportButton from 'components/button/ExportButton';
import { TanStackColumnFilter } from 'components/@extended/ColumnFilter';
import { TanStackColumnSorting } from 'components/@extended/SortingIconGroup';
import { SelectVisibleColumnV8 } from 'components/@extended/SelectVisibleColumn';
import { flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import {
	Box,
	Table,
	Button,
	TableRow,
	TableBody,
	TableCell,
	TableHead,
	TableContainer,
	// TablePagination,
	CircularProgress,
} from '@mui/material';
import {
	piningColumnFunc,
	visibleColumnFunc,
	getCommonPinningStyles,
	enhanceColumnsWithFiltering,
} from 'pages/comptroller/comptroller-table/tableUtility';

const tableVariants = {
	default: {
		table: {
			'& td, th': {
				borderRight: 1,
				borderColor: tableDesign.borderColor.primary,
			},

			'& th': {
				'&:first-of-type': { borderLeft: `1px solid ${tableDesign.borderColor.primary}` },
			},
		},
		loadingCell: { textAlign: 'center', height: '350px', border: `1px solid ${tableDesign.background.primary}` },
		tableMainCard: {
			mt: 2,
			width: '100%',
			borderRadius: tableDesign.borderRadius.primary,
			borderColor: tableDesign.borderColor.primary,
			border: 'none',
		},
		tableContainer: (pagination) => ({
			width: '100%',
			display: 'block',
			overflowX: 'auto',
			position: 'relative',
			mb: pagination ? '44px' : '0px',
			maxHeight: `calc(100svh - 357px)`,

			'& td, & th': { whiteSpace: 'nowrap' },
		}),
		tableBodyCell: {
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			borderBottom: '1px solid #E0E0E0',
			color: tableDesign.color.secondary,

			'&:first-of-type': { borderLeft: '1px solid', borderColor: tableDesign.borderColor.primary },
		},
		tableHeaderCell: {
			p: 0.5,
			top: '40px',
			height: '40px',
			position: 'sticky',
			padding: tableDesign.padding.default,
			backgroundColor: tableDesign.background.primary,

			'&:last-child': { borderColor: tableDesign.background.primary },
			'&:first-of-type': { backgroundColor: tableDesign.background.primary },

			'& .ant-input-affix-wrapper': { border: '1px solid #ced4da !important' },
		},
		tableHeaderSelectionCell: (header) => ({
			padding: tableDesign.padding.secondary,
			color: tableDesign.color.primary,
			backgroundColor: tableDesign.background.primary,

			...(header.id.toLowerCase() === 'selection' ? { width: '52px', textAlign: 'center' } : {}),
		}),
		tableHeaderRow: { height: '40px' },
		tableHeaderSelectionBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
		tableBodyRow: { cursor: 'pointer' },
		emptyTableBox: {
			width: '100%',
			height: '350px',
			display: 'flex',
			alignItems: 'center',
			flexDirection: 'column',
			justifyContent: 'center',
		},
		paginationBox: {
			left: 0,
			bottom: 0,
			width: '100%',
			position: 'absolute',
			backgroundColor: tableDesign.background.primary,
			// backgroundColor: 'blue',
		},
		pagination: {
			display: 'flex',
			justifyContent: 'center',

			'& p': { margin: 0 },
			'&.MuiToolbar-root': { pl: 1.5 },
			'.MuiTablePagination-toolbar': { py: 0, pr: 3, minHeight: '36px' },
			'.MuiTablePagination-toolbar p': { fontSize: tableFontSize.cellSize },

			'&.Mui-disabled': { color: 'red' },
			'&.MuiTablePagination-root': { color: tableDesign.color.primary },
			'.MuiTablePagination-selectIcon': { color: tableDesign.color.primary },
		},
	},
};

const RenderCustomButtons = ({ customButton, rowSelection }) => {
	return (
		<>
			{customButton && customButton.length > 0 && (
				<Box
					content="div"
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						alignItems: 'center',
						gap: { xs: '6px', md: '8px' },
					}}
				>
					{customButton.map((button, index) => (
						<Tooltip key={index} title={button.disabled ? button.disabledTextTooltip : button.title}>
							<Button
								type="primary"
								disabled={button.disabled}
								style={{
									backgroundColor: button.disabled ? 'gray' : button.color,
									color: button.disabled ? 'white' : (button?.textColor ?? '#000000'),
									padding: '8px 8px',
									fontSize: '14px',
									lineHeight: '18px',
								}}
								onClick={(e) => {
									e.stopPropagation();

									if (!button.disabled && typeof button.onClick === 'function') {
										button.onClick();
									}
								}}
							>
								{rowSelection?.length > 1 ? button.title + ' all' : button.title}
							</Button>
						</Tooltip>
					))}
				</Box>
			)}
		</>
	);
};

const DataTableV8 = ({
	DATA,
	pagination,
	COLUMNS = [],
	totalCount = 0,
	customButton = [],
	rowSelection = {},
	isLoading = false,
	onPaginationChange,
	columnFilters = [],
	variant = 'default',
	xlsxFileName = 'lynorg',
	manualFiltering = false,
	manualPagination = false,
	setSelectedRow = () => {},
	onClickHandler = () => {},
	setRowSelection = () => {},
	setColumnFilters = () => {},
}) => {
	const pageCount = totalCount ? Math.ceil(totalCount / pagination.pageSize) : -1;

	const table = useReactTable({
		pageCount,
		data: DATA,
		rowCount: totalCount,
		enableRowSelection: true,
		getCoreRowModel: getCoreRowModel(),
		onRowSelectionChange: setRowSelection,
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		columns: enhanceColumnsWithFiltering(COLUMNS),
		...(manualFiltering ? { manualFiltering } : {}), // 3. Set manualFiltering to true
		...(manualPagination ? { manualPagination } : {}), // 3. Set manualPagination to true
		...(manualFiltering ? { onColumnFiltersChange: setColumnFilters } : {}), // 4. Connect the updater function
		...(!manualPagination ? { getPaginationRowModel: getPaginationRowModel() } : {}), // 4. Add the pagination row model
		state: {
			pagination,
			rowSelection,
			...(manualFiltering ? { columnFilters: columnFilters } : {}), // 5. Pass the state to the table
		},
		initialState: {
			columnPinning: piningColumnFunc(COLUMNS),
			columnVisibility: visibleColumnFunc(COLUMNS),
		},
	});

	const dataExport = useCallback(() => {
		const headerArr = table
			?.getVisibleLeafColumns()
			?.map((item) => item.id)
			?.filter((item) => item !== 'Selection' && item !== 'activationStatus' && item !== 'Action' && item !== 'Status');

		const mainColumn = COLUMNS?.filter((item) => item?.id !== 'Selection' && item?.key !== 'activationStatus' && item?.key !== 'Action').reduce(
			(acc, col) => {
				acc[col.key] = col.header;
				return acc;
			},
			{}
		);

		const Header = headerArr.map((item) => {
			if (mainColumn?.[item]) {
				return mainColumn?.[item];
			} else {
				return item;
			}
		});

		const rows = table.getFilteredRowModel().rows;
		const rowsArr = rows.map((row) => {
			const rowObj = {};
			headerArr.forEach((colId) => {
				rowObj[colId] = row.getValue(colId);
			});
			return rowObj;
		});

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.json_to_sheet([]);
		XLSX.utils.sheet_add_aoa(ws, [Header]);
		XLSX.utils.sheet_add_json(ws, rowsArr, { origin: 'A2', skipHeader: true });

		Object.keys(ws).forEach((cell) => {
			if (cell[0] === '!') return;

			const cellObj = ws[cell];

			if (typeof cellObj.v === 'string') {
				const cleaned = cellObj.v.replace(/,/g, '');

				if (!isNaN(cleaned)) {
					cellObj.v = Number(cleaned);
					cellObj.t = 'n';
					cellObj.z = '#,##0.00';
				}
			}
		});

		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
		XLSX.writeFile(wb, `${generateExcelFileName(xlsxFileName)}.xlsx`);
	}, []);

	useEffect(() => {
		if (table?.getSelectedRowModel()?.flatRows?.length > 0) {
			setSelectedRow(table?.getSelectedRowModel()?.flatRows);
		}
	}, [table?.getSelectedRowModel()?.flatRows]);

	const styles = {
		table: { ...tableVariants.default.table, ...tableVariants[variant]?.table },
		pagination: { ...tableVariants.default.pagination, ...tableVariants[variant]?.pagination },
		loadingCell: { ...tableVariants.default.loadingCell, ...tableVariants[variant]?.loadingCell },
		tableBodyRow: { ...tableVariants.default.tableBodyRow, ...tableVariants[variant]?.tableBodyRow },
		emptyTableBox: { ...tableVariants.default.emptyTableBox, ...tableVariants[variant]?.emptyTableBox },
		tableMainCard: { ...tableVariants.default.tableMainCard, ...tableVariants[variant]?.tableMainCard },
		paginationBox: { ...tableVariants.default.paginationBox, ...tableVariants[variant]?.paginationBox },
		tableBodyCell: { ...tableVariants.default.tableBodyCell, ...tableVariants[variant]?.tableBodyCell },
		tableHeaderRow: { ...tableVariants.default.tableHeaderRow, ...tableVariants[variant]?.tableHeaderRow },
		tableHeaderCell: { ...tableVariants.default.tableHeaderCell, ...tableVariants[variant]?.tableHeaderCell },
		tableContainer: (pagination) => ({
			...tableVariants.default.tableContainer(pagination),
			...tableVariants[variant]?.tableContainer(pagination),
		}),
		tableHeaderSelectionBox: {
			...tableVariants.default.tableHeaderSelectionBox,
			...tableVariants[variant]?.tableHeaderSelectionBox,
		},
		tableHeaderSelectionCell: (header) => ({
			...tableVariants.default.tableHeaderSelectionCell(header),
			...tableVariants[variant]?.tableHeaderSelectionCell(header),
		}),
	};

	return (
		<>
			<Box content="div" sx={{ display: 'flex', justifyContent: 'space-between' }}>
				<SelectVisibleColumnV8 table={table} />

				<Flex align="center" gap=".5rem">
					{Object.keys(rowSelection)?.length > 0 && (
						<>
							<div>
								<b style={{ fontSize: '14px' }}>{Object.keys(rowSelection)?.length}</b>{' '}
								{Object.keys(rowSelection)?.length > 1 ? 'rows' : 'row'} selected
							</div>

							<RenderCustomButtons customButton={customButton} rowSelection={rowSelection} />
						</>
					)}

					<ExportButton handleDataExport={dataExport} />
				</Flex>
			</Box>

			<MainCard sx={styles.tableMainCard} content={false}>
				<TableContainer sx={styles.tableContainer(pagination)}>
					<Table stickyHeader sx={styles.table}>
						<TableHead>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id} sx={styles.tableHeaderRow}>
									{headerGroup.headers.map((header) => (
										<TableCell
											key={header.id}
											sx={{
												...getCommonPinningStyles(header.column, 'header'),

												...styles.tableHeaderSelectionCell(header),
											}}
										>
											{header.id === 'Selection' ? (
												header.isPlaceholder ? null : (
													flexRender(header.column.columnDef.header, header.getContext())
												)
											) : (
												<Box sx={styles.tableHeaderSelectionBox}>
													{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
													<TanStackColumnSorting header={header} />
												</Box>
											)}
										</TableCell>
									))}
								</TableRow>
							))}

							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<TableCell
											key={header.id}
											sx={{
												...getCommonPinningStyles(header.column, 'header'),

												...styles.tableHeaderCell,
											}}
										>
											{header.column.getCanFilter() ? <TanStackColumnFilter column={header.column} /> : null}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableHead>

						<TableBody
							sx={{
								'& tr:nth-of-type(even)': {
									backgroundColor: `${tableDesign.tableRowBackgroundColor.primary} !important`,
								},
							}}
						>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={COLUMNS.length} sx={styles.loadingCell}>
										<CircularProgress />
									</TableCell>
								</TableRow>
							) : table.getRowModel().rows?.length > 0 ? (
								table.getRowModel().rows.map((row) => (
									<TableRow key={row.id} sx={styles.tableBodyRow}>
										{row.getVisibleCells().map((cell) => (
											<TableCell
												key={cell.id}
												title={cell.getValue()}
												onClick={() => {
													if (cell?.column?.id !== 'action') {
														onClickHandler(row.original);
													}
												}}
												sx={{
													...getCommonPinningStyles(cell.column, 'body'),

													...styles.tableBodyCell,

													textAlign: cell.column.columnDef?.cellAlign ?? 'left',
													...(cell.column.columnDef?.dataType?.toLowerCase() === 'currency'
														? { textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
														: {}),
												}}
											>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</TableCell>
										))}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell
										colSpan={COLUMNS?.length}
										sx={{ ...styles.tableBodyCell, borderTop: `1px solid ${tableDesign.borderColor.primary}` }}
									>
										<Box component="div" sx={styles.emptyTableBox}>
											<Empty />
										</Box>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<Box sx={styles.paginationBox}>
					<TablePagination
						count={totalCount}
						// sx={styles.pagination}
						state={{ pageSize: pagination.pageSize, pageIndex: pagination.pageIndex }}
						gotoPage={(newPage) => {
							onPaginationChange((prev) => ({ ...prev, pageIndex: newPage }));
						}}
						setPageSize={(pageSize) => {
							onPaginationChange({
								pageSize,
								pageIndex: 0,
							});
						}}
					/>

					{/* <TablePagination
						component="div"
						count={totalCount}
						sx={styles.pagination}
						page={pagination.pageIndex}
						rowsPerPage={pagination.pageSize}
						rowsPerPageOptions={[15, 30, 50, 100]}
						onPageChange={(_, newPage) => {
							onPaginationChange((prev) => ({ ...prev, pageIndex: newPage }));
						}}
						onRowsPerPageChange={(event) => {
							onPaginationChange({
								pageIndex: 0,
								pageSize: parseInt(event.target.value),
							});
						}}
						SelectProps={{
							inputProps: {
								id: `pagination-select-50`,
							},
						}}
					/> */}
				</Box>
			</MainCard>
		</>
	);
};

export default DataTableV8;
